import { Box } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AutocompleteDropdown } from 'src/shared/components/form/autocompleteDropdown';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { InvoiceGroup } from 'src/shared/models/invoiceGroup/invoiceGroup';
import { InvoiceMissionResponse } from 'src/shared/models/invoiceMission/invoiceMission';
import { InvoiceMissionOptions } from 'src/shared/models/options/invoiceMissionOptions';
import { getAllInvoiceGroups } from 'src/shared/services/invoiceGroupService';
import invoiceMissionService from 'src/shared/services/invoiceMissionService';

type InvoiceGroupsAndMissionsProps = {
  initialInvoiceGroupId: number | undefined;
  initialInvoiceMissionId: number | undefined;
  callback: (invoiceGroupId: number | undefined, invoiceMissionId: number | undefined) => void;
};
export const InvoiceGroupsAndMissions = (props: InvoiceGroupsAndMissionsProps) => {
  const { callback, initialInvoiceGroupId, initialInvoiceMissionId } = props;
  const isMounted = useIsMounted();
  const { t } = useTranslation(['invoice']);
  const [invoiceGroups, setInvoiceGroups] = useState<InvoiceGroup[]>();
  const [invoiceGroupId, setInvoiceGroupId] = useState<number>();

  const [invoiceMissions, setInvoiceMissions] = useState<InvoiceMissionResponse[]>();
  const [invoiceMissionId, setInvoiceMissionId] = useState<number>();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await getAllInvoiceGroups(0, 500, 'name', 'asc', '');
        if (isMounted()) {
          setInvoiceGroups(result.data.results);
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
        }
      }
    };

    fetchData();
  }, [isMounted]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const options = new InvoiceMissionOptions();
        options.ownerId = invoiceGroupId;
        const result = await invoiceMissionService.getAll(options);
        if (isMounted()) {
          setInvoiceMissions(result.data.results);
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
        }
      }
    };
    if (invoiceGroupId) {
      fetchData();
    }
  }, [isMounted, invoiceGroupId]);

  const updateInvoiceGroup = useCallback(
    (val: number) => {
      callback(val, 0);
      setInvoiceGroupId(val);
      setInvoiceMissionId(undefined);
      setInvoiceMissions([]);
    },
    [callback],
  );

  const updateInvoiceMissions = (val: number) => {
    callback(invoiceGroupId, val);
    setInvoiceMissionId(val);
  };

  useEffect(() => {
    if (invoiceGroups?.length && initialInvoiceGroupId) setInvoiceGroupId(initialInvoiceGroupId);
    if (invoiceMissions?.length && initialInvoiceMissionId) setInvoiceMissionId(initialInvoiceMissionId);
    if (invoiceGroups?.length && invoiceGroups?.length === 1 && !initialInvoiceGroupId) updateInvoiceGroup(invoiceGroups[0].owner_id);
  }, [initialInvoiceGroupId, initialInvoiceMissionId, invoiceGroups, invoiceGroups?.length, invoiceMissions?.length, updateInvoiceGroup]);

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      {invoiceGroups && invoiceGroups.length > 0 && (
        <AutocompleteDropdown<InvoiceGroup>
          options={invoiceGroups}
          nameProp='name'
          keyProp='name'
          name='invoiceGroup'
          value={invoiceGroupId ? invoiceGroups.filter((x) => x.owner_id === invoiceGroupId)[0] : undefined}
          label={t('invoice:invoiceGroups')}
          callback={(val) => updateInvoiceGroup(val && val.owner_id ? val.owner_id : 0)}
          componentWidth='15rem'
        />
      )}
      <Box>
        <AutocompleteDropdown<InvoiceMissionResponse>
          options={invoiceMissions || []}
          nameProp='missionName'
          keyProp='invoiceMission_id'
          name='invoiceMission'
          value={invoiceMissionId ? invoiceMissions?.filter((x) => x.invoiceMission_id === invoiceMissionId)[0] : undefined}
          label={t('invoice:invoiceMission')}
          callback={(val) => updateInvoiceMissions(val && val.invoiceMission_id ? val.invoiceMission_id : 0)}
          disable={!invoiceGroupId}
          componentWidth='15rem'
        />
      </Box>
    </Box>
  );
};
